// src/pages/Tasks/TaskReports.tsx

import React, { useState, useEffect, useCallback } from 'react';
import {
  ListChecks, CheckCircle2, AlertTriangle, Clock, TrendingUp, Activity,
  Calendar as CalIcon, Zap, Award, BarChart3, Users as UsersIcon, FileText,
  Building2, Sparkles, Target, ArrowUpRight, ArrowDownRight, Folder,
} from 'lucide-react';
import {
  getTaskOverview,
  getEmployeePerformance,
  getTaskListReport,
  getWorkloadReport,
  getTimelineReport,
  getClientReport,
  getClientTasks,
  getProjectReport,
  getProjectTasks,
} from '../api/taskreport';
import {
  OverviewStats,
  EmployeePerformance,
  TaskReport,
  WorkloadItem,
  TimelinePoint,
  ReportFilters,
  TabType,
  ClientReportItem,
  ClientTaskItem,
  ProjectReportItem,
  ProjectTaskItem,
} from '../types/taskReport';
import { useNavigate } from "react-router-dom";
import { displayedProgress } from '../utils/taskStatus';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { DateField } from '../components/ui/DateField';

function TaskReports() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TabType>('overview');
  const [filters, setFilters] = useState<ReportFilters>({});
  const [tempFilters, setTempFilters] = useState<ReportFilters>({});

  // State for each tab
  const [overview, setOverview] = useState<OverviewStats | null>(null);
  const [employees, setEmployees] = useState<EmployeePerformance[]>([]);
  const [tasks, setTasks] = useState<TaskReport[]>([]);
  const [workload, setWorkload] = useState<WorkloadItem[]>([]);
  const [timeline, setTimeline] = useState<TimelinePoint[]>([]);
  const [timelineGroupBy, setTimelineGroupBy] = useState<'month' | 'week'>('month');

  // Clients tab state
  const [clientReport, setClientReport] = useState<ClientReportItem[]>([]);
  const [loadingClients, setLoadingClients] = useState(false);
  const [selectedClientId, setSelectedClientId] = useState<string | null>(null);
  const [selectedClientName, setSelectedClientName] = useState('');
  const [clientTasks, setClientTasks] = useState<ClientTaskItem[]>([]);
  const [loadingClientTasks, setLoadingClientTasks] = useState(false);
  const [clientTaskStatusFilter, setClientTaskStatusFilter] = useState('');

  // Projects tab state
  const [projectReport, setProjectReport] = useState<ProjectReportItem[]>([]);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null);
  const [selectedProjectName, setSelectedProjectName] = useState('');
  const [projectTasks, setProjectTasks] = useState<ProjectTaskItem[]>([]);
  const [loadingProjectTasks, setLoadingProjectTasks] = useState(false);
  const [projectTaskStatusFilter, setProjectTaskStatusFilter] = useState('');

  // Pagination state
  const [taskPage, setTaskPage] = useState(1);
  const [taskPageSize, setTaskPageSize] = useState(20);
  const [totalTasks, setTotalTasks] = useState(0);

  // Loading states
  const [loadingOverview, setLoadingOverview] = useState(false);
  const [loadingEmployees, setLoadingEmployees] = useState(false);
  const [loadingTasks, setLoadingTasks] = useState(false);
  const [loadingWorkload, setLoadingWorkload] = useState(false);
  const [loadingTimeline, setLoadingTimeline] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Modal state for employee details
  const [selectedEmployee, setSelectedEmployee] = useState<EmployeePerformance | null>(null);
  const [isEmployeeModalOpen, setIsEmployeeModalOpen] = useState(false);

  // Load data based on active tab and filters
  const loadOverview = useCallback(async () => {
    setLoadingOverview(true);
    setError(null);
    try {
      const data = await getTaskOverview(filters);
      setOverview(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load overview');
    } finally {
      setLoadingOverview(false);
    }
  }, [filters]);

  const loadEmployees = useCallback(async () => {
    setLoadingEmployees(true);
    setError(null);
    try {
      const data = await getEmployeePerformance(filters);
      setEmployees(data.employees || []);
    } catch (err: any) {
      setError(err.message || 'Failed to load employee performance');
    } finally {
      setLoadingEmployees(false);
    }
  }, [filters]);

  const loadTasks = useCallback(async () => {
    setLoadingTasks(true);
    setError(null);
    try {
      const data = await getTaskListReport({
        ...filters,
        page: taskPage,
        page_size: taskPageSize,
        include_closed: true,
      });
      setTasks(data.tasks || []);
      setTotalTasks(data.total || 0);
    } catch (err: any) {
      setError(err.message || 'Failed to load tasks');
    } finally {
      setLoadingTasks(false);
    }
  }, [filters, taskPage, taskPageSize]);

  const loadWorkload = useCallback(async () => {
    setLoadingWorkload(true);
    setError(null);
    try {
      const data = await getWorkloadReport({ department_id: filters.department_id });
      setWorkload(data.workload || []);
    } catch (err: any) {
      setError(err.message || 'Failed to load workload');
    } finally {
      setLoadingWorkload(false);
    }
  }, [filters.department_id]);

  const loadTimeline = useCallback(async () => {
    setLoadingTimeline(true);
    setError(null);
    try {
      const data = await getTimelineReport({
        ...filters,
        group_by: timelineGroupBy,
      });
      setTimeline(data.timeline || []);
    } catch (err: any) {
      setError(err.message || 'Failed to load timeline');
    } finally {
      setLoadingTimeline(false);
    }
  }, [filters, timelineGroupBy]);

  const loadProjects = useCallback(async () => {
    setLoadingProjects(true);
    setError(null);
    try {
      const data = await getProjectReport({ start_date: filters.start_date, end_date: filters.end_date });
      setProjectReport(data.items || []);
    } catch (err: any) {
      setError(err.message || 'Failed to load project report');
    } finally {
      setLoadingProjects(false);
    }
  }, [filters.start_date, filters.end_date]);

  const loadProjectTasksFor = async (projectId: string, statusF: string) => {
    setLoadingProjectTasks(true);
    try {
      const data = await getProjectTasks(projectId, {
        start_date: filters.start_date,
        end_date: filters.end_date,
        status: statusF || undefined,
      });
      setProjectTasks(data.tasks || []);
    } catch { setProjectTasks([]); }
    finally { setLoadingProjectTasks(false); }
  };

  const loadClients = useCallback(async () => {
    setLoadingClients(true);
    setError(null);
    try {
      const data = await getClientReport({ start_date: filters.start_date, end_date: filters.end_date });
      setClientReport(data.items || []);
    } catch (err: any) {
      setError(err.message || 'Failed to load client report');
    } finally {
      setLoadingClients(false);
    }
  }, [filters]);

  const loadClientTasksFor = async (clientId: string, statusF: string) => {
    setLoadingClientTasks(true);
    try {
      const data = await getClientTasks(clientId, {
        start_date: filters.start_date,
        end_date: filters.end_date,
        status: statusF || undefined,
      });
      setClientTasks(data.tasks || []);
    } catch { setClientTasks([]); }
    finally { setLoadingClientTasks(false); }
  };

  useEffect(() => {
    switch (activeTab) {
      case 'overview':
        loadOverview();
        break;
      case 'employees':
        loadEmployees();
        break;
      case 'tasks':
        loadTasks();
        break;
      case 'workload':
        loadWorkload();
        break;
      case 'timeline':
        loadTimeline();
        break;
      case 'clients':
        loadClients();
        break;
      case 'projects':
        loadProjects();
        break;
    }
  }, [activeTab, loadOverview, loadEmployees, loadTasks, loadWorkload, loadTimeline, loadClients, loadProjects]);

  const handleFilterChange = (key: keyof ReportFilters, value: string) => {
    setTempFilters(prev => ({ ...prev, [key]: value || undefined }));
  };

  const applyFilters = () => {
    setFilters(tempFilters);
    setTaskPage(1);
  };

  const resetFilters = () => {
    setTempFilters({});
    setFilters({});
    setTaskPage(1);
  };

  const handlePageChange = (newPage: number) => {
    setTaskPage(newPage);
    window.scrollTo(0, 0);
  };

  const handlePageSizeChange = (newSize: number) => {
    setTaskPageSize(newSize);
    setTaskPage(1);
  };

  const totalPages = Math.ceil(totalTasks / taskPageSize);

  // Render Overview Tab
  const renderOverview = () => {
    if (loadingOverview) {
      return (
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="gt-loading-ring mx-auto mb-3" />
            <p className="gt-page-sub gt-serif-italic">Loading <em>overview</em>…</p>
          </div>
        </div>
      );
    }

    if (!overview) return null;

    const completion = Number(overview.completion_rate_pct ?? 0);
    const overdue = Number(overview.overdue_rate_pct ?? 0);

    // ─── Hero stat cards (4 large coloured tiles with mini-progress ring)
    const heroCards: Array<{ label: string; value: string | number; hint?: string; icon: React.ReactNode; tone: 'all' | 'done' | 'alert' | 'time' }> = [
      { label: 'Total Tasks',     value: overview.total_tasks,                          icon: <ListChecks className="w-5 h-5" />,    tone: 'all' },
      { label: 'Completion Rate', value: `${completion}%`,                              icon: <CheckCircle2 className="w-5 h-5" />,  tone: 'done',  hint: completion >= 70 ? 'Excellent' : completion >= 40 ? 'On track' : 'Needs push' },
      { label: 'Overdue Rate',    value: `${overdue}%`,                                  icon: <AlertTriangle className="w-5 h-5" />, tone: 'alert', hint: overdue > 0 ? `${overview.overdue_count} task${overview.overdue_count === 1 ? '' : 's'}` : 'all clear' },
      { label: 'Avg Completion',  value: `${overview.avg_completion_days ?? 0}`,        icon: <Clock className="w-5 h-5" />,         tone: 'time',  hint: 'days · per task' },
    ];

    const TONES: Record<string, { bg: string; border: string; iconBg: string; iconFg: string; label: string; value: string }> = {
      all:   { bg: '#d1fae5', border: '#86efac', iconBg: '#bbf7d0', iconFg: '#047857', label: '#047857', value: '#064e3b' },
      done:  { bg: '#dbeafe', border: '#93c5fd', iconBg: '#bfdbfe', iconFg: '#1d4ed8', label: '#1e40af', value: '#1e3a8a' },
      alert: { bg: '#fee2e2', border: '#fca5a5', iconBg: '#fecaca', iconFg: '#b91c1c', label: '#b91c1c', value: '#7f1d1d' },
      time:  { bg: '#fef3c7', border: '#fcd34d', iconBg: '#fde68a', iconFg: '#b45309', label: '#92400e', value: '#78350f' },
    };

    // Status / priority colour maps
    const statusBar: Record<string, string> = {
      DONE: 'linear-gradient(90deg, #5eead4 0%, #14b890 50%, #047857 100%)',
      IN_PROGRESS: 'linear-gradient(90deg, #60a5fa 0%, #2563eb 100%)',
      BLOCKED: 'linear-gradient(90deg, #fca5a5 0%, #ef4444 100%)',
      TODO: 'linear-gradient(90deg, #cbd5e1 0%, #64748b 100%)',
      IN_REVIEW: 'linear-gradient(90deg, #fcd34d 0%, #f59e0b 100%)',
      CANCELLED: 'linear-gradient(90deg, #e2e8f0 0%, #94a3b8 100%)',
    };
    const prioBar: Record<string, string> = {
      URGENT: 'linear-gradient(90deg, #fca5a5 0%, #ef4444 100%)',
      HIGH:   'linear-gradient(90deg, #fdba74 0%, #f97316 100%)',
      MEDIUM: 'linear-gradient(90deg, #fde68a 0%, #f59e0b 100%)',
      LOW:    'linear-gradient(90deg, #6ee7b7 0%, #10b981 100%)',
    };

    // Donut chart for completion rate
    const donutR = 56;
    const donutC = 2 * Math.PI * donutR;

    return (
      <div className="space-y-6 gt-fade-in">

        {/* ─── Hero strip moved to bottom — see below */}
        {false && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {heroCards.map((c) => {
            const t = TONES[c.tone];
            return (
              <div key={c.label} style={{
                background: t.bg,
                border: `1px solid ${t.border}`,
                borderRadius: 16,
                padding: '18px 20px',
                boxShadow: 'var(--gt-elev-1)',
                position: 'relative',
                overflow: 'hidden',
                transition: 'transform 200ms cubic-bezier(0.33, 1, 0.68, 1), box-shadow 200ms cubic-bezier(0.33, 1, 0.68, 1)',
              }}
                onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = 'var(--gt-elev-3)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = 'var(--gt-elev-1)'; }}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <span className="gt-eyebrow" style={{ color: t.label, marginBottom: 0, fontSize: 10 }}>{c.label}</span>
                    <p className="gt-display gt-numerals" style={{ fontSize: 30, fontWeight: 800, letterSpacing: '-0.03em', color: t.value, marginTop: 6, lineHeight: 1 }}>
                      {c.value}
                    </p>
                    {c.hint && (
                      <p className="gt-page-sub gt-serif-italic" style={{ marginTop: 6, color: t.label, fontSize: 11.5 }}>{c.hint}</p>
                    )}
                  </div>
                  <span style={{
                    width: 40, height: 40, borderRadius: 12,
                    background: t.iconBg, color: t.iconFg,
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                    boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.55)',
                  }}>
                    {c.icon}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
        )}

        {/* ─── Donut + Period summary band */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Big completion donut */}
          <div className="gt-card p-5 lg:col-span-1" style={{ position: 'relative' }}>
            <div className="flex items-center gap-2 mb-3">
              <span className="gt-leaf" />
              <span className="gt-eyebrow">i. Performance</span>
            </div>
            <h3 className="gt-display" style={{ fontSize: 16, color: 'var(--gt-text-primary)', marginBottom: 14 }}>
              Completion <em className="gt-serif-italic" style={{ color: 'var(--gt-accent-strong)' }}>Health</em>
            </h3>

            <div className="flex items-center justify-center" style={{ padding: '12px 0' }}>
              <div style={{ position: 'relative', width: 160, height: 160 }}>
                <svg width={160} height={160} viewBox="0 0 160 160" style={{ transform: 'rotate(-90deg)' }}>
                  <defs>
                    <linearGradient id="gt-overview-ring" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%"  stopColor="#5eead4" />
                      <stop offset="50%" stopColor="#14b890" />
                      <stop offset="100%" stopColor="#047857" />
                    </linearGradient>
                  </defs>
                  <circle cx={80} cy={80} r={donutR} fill="none" stroke="var(--gt-surface-2)" strokeWidth={12} />
                  <circle
                    cx={80} cy={80} r={donutR} fill="none"
                    stroke="url(#gt-overview-ring)"
                    strokeWidth={12} strokeLinecap="round"
                    strokeDasharray={donutC}
                    strokeDashoffset={donutC * (1 - completion / 100)}
                    style={{ transition: 'stroke-dashoffset 800ms cubic-bezier(0.4, 0, 0.2, 1)', filter: 'drop-shadow(0 4px 12px rgba(20, 184, 144, 0.35))' }}
                  />
                </svg>
                <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                  <span className="gt-display gt-numerals" style={{ fontSize: 36, fontWeight: 800, letterSpacing: '-0.03em', color: 'var(--gt-text-primary)', lineHeight: 1 }}>
                    {completion}<span style={{ fontSize: 18, color: 'var(--gt-text-tertiary)' }}>%</span>
                  </span>
                  <span className="gt-eyebrow" style={{ marginTop: 4, marginBottom: 0 }}>completed</span>
                </div>
              </div>
            </div>

            <div className="flex justify-between gt-page-sub gt-numerals" style={{ marginTop: 12, fontSize: 11.5 }}>
              <span><span style={{ color: 'var(--gt-accent-strong)' }}>●</span> {overview.tasks_completed_in_period} done</span>
              <span style={{ color: 'var(--gt-text-tertiary)' }}><span style={{ color: '#94a3b8' }}>●</span> {(overview.total_tasks || 0) - (overview.tasks_completed_in_period || 0)} open</span>
            </div>
          </div>

          {/* Period KPIs as 2x2 mini cards */}
          <div className="lg:col-span-2 grid grid-cols-2 gap-4">
            {[
              { label: 'Created in Period',   value: overview.tasks_created_in_period,   icon: <Sparkles className="w-4 h-4" />,      delta: 'new entries' },
              { label: 'Completed in Period', value: overview.tasks_completed_in_period, icon: <Award className="w-4 h-4" />,         delta: 'finished work' },
              { label: 'Avg Open Progress',   value: `${overview.avg_open_progress_pct}%`, icon: <TrendingUp className="w-4 h-4" />,  delta: 'across active tasks' },
              { label: 'Overdue Tasks',       value: overview.overdue_count,             icon: <AlertTriangle className="w-4 h-4" />, delta: overview.overdue_count > 0 ? 'needs attention' : 'all clear', alert: true },
            ].map((m) => (
              <div key={m.label} className="gt-card p-5">
                <div className="flex items-start justify-between mb-2">
                  <span className="gt-eyebrow" style={{ marginBottom: 0 }}>{m.label}</span>
                  <span style={{
                    width: 28, height: 28, borderRadius: 9,
                    background: m.alert && Number(m.value) > 0 ? '#fee2e2' : 'var(--gt-primary-container)',
                    color: m.alert && Number(m.value) > 0 ? '#b91c1c' : 'var(--gt-primary-deep)',
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.5)',
                  }}>
                    {m.icon}
                  </span>
                </div>
                <p className="gt-display gt-numerals" style={{
                  fontSize: 28, fontWeight: 800, letterSpacing: '-0.025em',
                  color: m.alert && Number(m.value) > 0 ? '#7f1d1d' : 'var(--gt-text-primary)',
                  lineHeight: 1, marginTop: 4,
                }}>
                  {m.value}
                </p>
                <p className="gt-page-sub" style={{ fontSize: 11.5, marginTop: 6 }}>{m.delta}</p>
              </div>
            ))}
          </div>
        </div>

        {/* ─── Status + Priority breakdown */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="gt-card p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Activity className="w-4 h-4" style={{ color: 'var(--gt-accent-strong)' }} />
                <h3 className="gt-display" style={{ fontSize: 15, color: 'var(--gt-text-primary)' }}>Status Breakdown</h3>
              </div>
              <span className="gt-pill is-ghost gt-numerals">{Object.keys(overview.by_status).length} states</span>
            </div>
            <div className="space-y-3.5">
              {Object.entries(overview.by_status).map(([status, count]) => {
                const percentage = overview.total_tasks ? (count / overview.total_tasks) * 100 : 0;
                return (
                  <div key={status}>
                    <div className="flex justify-between gt-numerals" style={{ fontSize: 12.5, marginBottom: 6 }}>
                      <span className="gt-display" style={{ fontWeight: 600, color: 'var(--gt-text-secondary)' }}>
                        {status.toLowerCase().replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                      </span>
                      <span style={{ color: 'var(--gt-text-tertiary)' }}>
                        <span className="gt-display" style={{ color: 'var(--gt-text-primary)' }}>{count}</span>
                        {' '}· {percentage.toFixed(1)}%
                      </span>
                    </div>
                    <div className="gt-progress-track" style={{ height: 8 }}>
                      <div style={{ height: '100%', borderRadius: 999, background: statusBar[status] || statusBar.TODO, width: `${percentage}%`, boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.45)', transition: 'width 600ms cubic-bezier(0.4,0,0.2,1)' }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="gt-card p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Target className="w-4 h-4" style={{ color: 'var(--gt-accent-strong)' }} />
                <h3 className="gt-display" style={{ fontSize: 15, color: 'var(--gt-text-primary)' }}>Priority Breakdown</h3>
              </div>
              <span className="gt-pill is-ghost gt-numerals">{Object.keys(overview.by_priority).length} levels</span>
            </div>
            <div className="space-y-3.5">
              {Object.entries(overview.by_priority).map(([priority, count]) => {
                const percentage = overview.total_tasks ? (count / overview.total_tasks) * 100 : 0;
                return (
                  <div key={priority}>
                    <div className="flex justify-between gt-numerals" style={{ fontSize: 12.5, marginBottom: 6 }}>
                      <span className="gt-display" style={{ fontWeight: 700, color: 'var(--gt-text-secondary)' }}>{priority}</span>
                      <span style={{ color: 'var(--gt-text-tertiary)' }}>
                        <span className="gt-display" style={{ color: 'var(--gt-text-primary)' }}>{count}</span>
                        {' '}· {percentage.toFixed(1)}%
                      </span>
                    </div>
                    <div className="gt-progress-track" style={{ height: 8 }}>
                      <div style={{ height: '100%', borderRadius: 999, background: prioBar[priority] || prioBar.MEDIUM, width: `${percentage}%`, boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.45)', transition: 'width 600ms cubic-bezier(0.4,0,0.2,1)' }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* ─── Hero summary strip — moved to the bottom of the overview */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {heroCards.map((c) => {
            const t = TONES[c.tone];
            return (
              <div key={c.label} style={{
                background: t.bg,
                border: `1px solid ${t.border}`,
                borderRadius: 16,
                padding: '18px 20px',
                boxShadow: 'var(--gt-elev-1)',
                position: 'relative',
                overflow: 'hidden',
                transition: 'transform 200ms cubic-bezier(0.33, 1, 0.68, 1), box-shadow 200ms cubic-bezier(0.33, 1, 0.68, 1)',
              }}
                onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = 'var(--gt-elev-3)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = 'var(--gt-elev-1)'; }}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <span className="gt-eyebrow" style={{ color: t.label, marginBottom: 0, fontSize: 10 }}>{c.label}</span>
                    <p className="gt-display gt-numerals" style={{ fontSize: 30, fontWeight: 800, letterSpacing: '-0.03em', color: t.value, marginTop: 6, lineHeight: 1 }}>
                      {c.value}
                    </p>
                    {c.hint && (
                      <p className="gt-page-sub gt-serif-italic" style={{ marginTop: 6, color: t.label, fontSize: 11.5 }}>{c.hint}</p>
                    )}
                  </div>
                  <span style={{
                    width: 40, height: 40, borderRadius: 12,
                    background: t.iconBg, color: t.iconFg,
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                    boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.55)',
                  }}>
                    {c.icon}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // Render Employees Tab
  const renderEmployees = () => {
    if (loadingEmployees) {
      return (
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-500">Loading employee data...</p>
          </div>
        </div>
      );
    }

    if (employees.length === 0) {
      return (
        <div className="bg-white rounded-2xl shadow-xl p-12 text-center">
          <div className="w-32 h-32 bg-gradient-to-br from-gray-100 to-gray-200 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg className="w-16 h-16 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
          </div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">No employees found</h3>
          <p className="text-gray-500">Try adjusting your filters</p>
        </div>
      );
    }

    const getCompletionColor = (rate: number) => {
      if (rate >= 80) return 'text-green-600 bg-green-100';
      if (rate >= 50) return 'text-yellow-600 bg-yellow-100';
      return 'text-red-600 bg-red-100';
    };

    return (
      <div className="bg-white rounded-xl shadow-md border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Employee</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Assigned</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Done</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Open</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Overdue</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Blocked</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Completion</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {employees.map((employee) => (
                <tr key={employee.user_id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      {/* <div className="font-medium text-gray-900">{employee.name}</div> */}
                      <div
                        onClick={() =>
                          navigate(`/tasks/list?assigned_to=${employee.user_id}&include_closed=true`)
                        }
                        className="font-medium text-blue-600 cursor-pointer hover:underline"
                      >
                        {employee.name}
                      </div>
                      <div className="text-sm text-gray-500">{employee.email}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{employee.total_assigned}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600 font-medium">{employee.done}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{employee.open}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-red-600">{employee.overdue}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-orange-600">{employee.blocked}</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${getCompletionColor(employee.completion_rate_pct)}`}>
                      {employee.completion_rate_pct}%
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <button
                      onClick={() =>
                        navigate(`/tasks/list?assigned_to=${employee.user_id}&include_closed=true`)
                      }
                      className="text-blue-600 hover:text-blue-800 font-medium"
                    >
                      View Details
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  // Render Tasks Tab
  const renderTasks = () => {
    if (loadingTasks) {
      return (
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-500">Loading tasks...</p>
          </div>
        </div>
      );
    }

    if (tasks.length === 0) {
      return (
        <div className="bg-white rounded-2xl shadow-xl p-12 text-center">
          <div className="w-32 h-32 bg-gradient-to-br from-gray-100 to-gray-200 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg className="w-16 h-16 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
          </div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">No tasks found</h3>
          <p className="text-gray-500">Try adjusting your filters</p>
        </div>
      );
    }

    const getPriorityColor = (priority: string) => {
      const colors: Record<string, string> = {
        URGENT: 'bg-red-100 text-red-800',
        HIGH: 'bg-orange-100 text-orange-800',
        MEDIUM: 'bg-yellow-100 text-yellow-800',
        LOW: 'bg-blue-100 text-blue-800',
      };
      return colors[priority] || 'bg-gray-100 text-gray-800';
    };

    const getStatusColor = (status: string) => {
      const colors: Record<string, string> = {
        DONE: 'bg-green-100 text-green-800',
        IN_PROGRESS: 'bg-blue-100 text-blue-800',
        BLOCKED: 'bg-red-100 text-red-800',
        TODO: 'bg-gray-100 text-gray-800',
        IN_REVIEW: 'bg-purple-100 text-purple-800',
      };
      return colors[status] || 'bg-yellow-100 text-yellow-800';
    };

    return (
      <>
        <div className="mb-4 flex justify-between items-center">
          <p className="text-sm text-gray-500">
            Showing <span className="font-medium text-gray-700">{tasks.length}</span> of{" "}
            <span className="font-medium text-gray-700">{totalTasks}</span> tasks
          </p>
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-600">Show:</label>
            <div className="w-[140px]">
              <Select
                value={String(taskPageSize)}
                onValueChange={(v) => handlePageSizeChange(Number(v))}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {[10, 20, 50, 100].map((size) => (
                    <SelectItem key={size} value={String(size)}>{size} per page</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-md border border-gray-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Title</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Priority</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Assignees</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Checklist</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Progress</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Due Date</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {tasks.map((task) => (
                  <tr key={task.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4">
                      <div
                        onClick={() => navigate(`/tasks/${task.id}`)}
                        className="font-medium text-blue-600 cursor-pointer hover:underline"
                      >
                        {task.title}
                      </div>
                      <div className="text-xs text-gray-500">
                        Created: {task.created_at ? new Date(task.created_at).toLocaleDateString() : '-'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(task.status)}`}>
                        {task.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs rounded-full ${getPriorityColor(task.priority)}`}>
                        {task.priority}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-wrap gap-1">
                        {task.assignees.slice(0, 2).map((a) => (
                          <span key={a.user_id} className="text-xs bg-gray-100 px-2 py-1 rounded">
                            {a.name}
                          </span>
                        ))}
                        {task.assignees.length > 2 && (
                          <span className="text-xs text-gray-500">+{task.assignees.length - 2}</span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {task.checklist_total > 0 ? `${task.checklist_done}/${task.checklist_total}` : '-'}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <div className="w-20 bg-gray-200 rounded-full h-2">
                          <div className="bg-blue-600 h-2 rounded-full" style={{ width: `${displayedProgress(task.progress_percent, task.status)}%` }} />
                        </div>
                        <span className="text-xs text-gray-600">{displayedProgress(task.progress_percent, task.status)}%</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {task.due_date ? (
                        <span className={task.is_overdue ? 'text-red-600 font-medium' : 'text-gray-600'}>
                          {new Date(task.due_date).toLocaleDateString()}
                        </span>
                      ) : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <>
            <div className="mt-6 flex justify-center items-center gap-2">
              <button
                onClick={() => handlePageChange(taskPage - 1)}
                disabled={taskPage === 1}
                className="px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2 shadow-sm"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                Previous
              </button>

              <div className="flex items-center gap-2">
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (taskPage <= 3) {
                    pageNum = i + 1;
                  } else if (taskPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = taskPage - 2 + i;
                  }

                  return (
                    <button
                      key={pageNum}
                      onClick={() => handlePageChange(pageNum)}
                      className={`w-10 h-10 rounded-lg transition-all duration-200 shadow-sm ${taskPage === pageNum
                          ? 'bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-md'
                          : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                        }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>

              <button
                onClick={() => handlePageChange(taskPage + 1)}
                disabled={taskPage === totalPages}
                className="px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2 shadow-sm"
              >
                Next
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </>
        )}
      </>
    );
  };

  // Render Workload Tab
  const renderWorkload = () => {
    if (loadingWorkload) {
      return (
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-500">Loading workload data...</p>
          </div>
        </div>
      );
    }

    if (workload.length === 0) {
      return (
        <div className="bg-white rounded-2xl shadow-xl p-12 text-center">
          <div className="w-32 h-32 bg-gradient-to-br from-gray-100 to-gray-200 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg className="w-16 h-16 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
            </svg>
          </div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">No workload data</h3>
          <p className="text-gray-500">No employees have open tasks</p>
        </div>
      );
    }

    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {workload.map((employee) => (
          <div
            key={employee.user_id}
            onClick={() =>
              navigate(`/tasks/list?assigned_to=${employee.user_id}&include_closed=true`)
            }
            className="bg-white rounded-xl shadow-md border border-gray-100 p-5 hover:shadow-lg transition-shadow cursor-pointer"
          >
            <div className="flex justify-between items-start mb-4">
              <div>
                <h4 className="font-semibold text-gray-900">{employee.name}</h4>
                <p className="text-xs text-gray-500 mt-1">{employee.email}</p>
              </div>
              <span className="bg-blue-100 text-blue-800 text-sm font-semibold px-3 py-1 rounded-full">
                {employee.open_tasks} open
              </span>
            </div>

            <div className="space-y-2 mb-4">
              {employee.overdue > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-red-600">Overdue:</span>
                  <span className="font-medium text-red-600">{employee.overdue}</span>
                </div>
              )}
              {employee.blocked > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-orange-600">Blocked:</span>
                  <span className="font-medium text-orange-600">{employee.blocked}</span>
                </div>
              )}
              {employee.in_review > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-purple-600">In Review:</span>
                  <span className="font-medium text-purple-600">{employee.in_review}</span>
                </div>
              )}
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Avg Progress:</span>
                <span className="font-medium text-gray-900">{employee.avg_progress_pct}%</span>
              </div>
            </div>

            <div className="pt-3 border-t border-gray-100">
              <p className="text-xs text-gray-500 mb-2">Priority Breakdown</p>
              <div className="flex flex-wrap gap-2">
                {employee.by_priority.urgent > 0 && (
                  <span className="bg-red-100 text-red-800 text-xs px-2 py-1 rounded-full">
                    Urgent: {employee.by_priority.urgent}
                  </span>
                )}
                {employee.by_priority.high > 0 && (
                  <span className="bg-orange-100 text-orange-800 text-xs px-2 py-1 rounded-full">
                    High: {employee.by_priority.high}
                  </span>
                )}
                {employee.by_priority.medium > 0 && (
                  <span className="bg-yellow-100 text-yellow-800 text-xs px-2 py-1 rounded-full">
                    Medium: {employee.by_priority.medium}
                  </span>
                )}
                {employee.by_priority.low > 0 && (
                  <span className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full">
                    Low: {employee.by_priority.low}
                  </span>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  // Render Timeline Tab
  const renderTimeline = () => {
    if (loadingTimeline) {
      return (
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="gt-loading-ring mx-auto mb-3" />
            <p className="gt-page-sub gt-serif-italic">Loading <em>timeline</em>…</p>
          </div>
        </div>
      );
    }

    if (timeline.length === 0) {
      return (
        <div className="gt-card p-12">
          <div className="gt-empty">
            <div className="gt-empty-leaf"><CalIcon className="w-7 h-7" /></div>
            <div className="gt-empty-title">No timeline data</div>
            <div className="gt-page-sub">No tasks found in the <em className="gt-serif-italic">selected</em> date range.</div>
          </div>
        </div>
      );
    }

    const totalCreated = timeline.reduce((s, d) => s + d.created, 0);
    const totalCompleted = timeline.reduce((s, d) => s + d.completed, 0);
    const totalOverdue = timeline.reduce((s, d) => s + d.overdue, 0);
    const maxValue = Math.max(...timeline.flatMap(d => [d.created, d.completed, d.overdue]), 1);
    const peakPoint = timeline.reduce((peak, d) => (d.created + d.completed + d.overdue) > (peak.created + peak.completed + peak.overdue) ? d : peak, timeline[0]);

    const fmtDate = (iso: string) =>
      new Date(iso).toLocaleDateString('en-US', timelineGroupBy === 'month' ? { month: 'short', year: '2-digit' } : { month: 'short', day: 'numeric' });

    return (
      <div className="space-y-4 gt-fade-in">

        {/* ─── KPI strip */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {[
            { label: 'Created',   value: totalCreated,   bg: '#dbeafe', border: '#93c5fd', dot: '#2563eb', value_c: '#1e3a8a', label_c: '#1e40af' },
            { label: 'Completed', value: totalCompleted, bg: '#bbf7d0', border: '#4ade80', dot: '#10b981', value_c: '#064e3b', label_c: '#047857' },
            { label: 'Overdue',   value: totalOverdue,   bg: '#fecaca', border: '#f87171', dot: '#ef4444', value_c: '#7f1d1d', label_c: '#991b1b' },
          ].map((k) => (
            <div key={k.label} style={{
              background: k.bg, border: `1px solid ${k.border}`, borderRadius: 16, padding: '14px 18px',
              boxShadow: 'var(--gt-elev-1)',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
            }}>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                  <span style={{ width: 9, height: 9, borderRadius: 999, background: k.dot, boxShadow: `0 0 0 3px ${k.bg}` }} />
                  <span className="gt-eyebrow" style={{ color: k.label_c, marginBottom: 0, fontSize: 10 }}>{k.label}</span>
                </div>
                <p className="gt-display gt-numerals" style={{ fontSize: 28, fontWeight: 800, letterSpacing: '-0.025em', color: k.value_c, lineHeight: 1, margin: 0 }}>
                  {k.value}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* ─── Big stacked-bar timeline */}
        <div className="gt-card p-6">
          <div className="flex justify-between items-start mb-5 flex-wrap gap-3">
            <div>
              <div className="gt-eyebrow-row" style={{ marginBottom: 4 }}>
                <span className="gt-leaf" />
                <span className="gt-eyebrow">i. Trend</span>
              </div>
              <h3 className="gt-display" style={{ fontSize: 17, color: 'var(--gt-text-primary)' }}>
                Task <em className="gt-serif-italic" style={{ color: 'var(--gt-accent-strong)' }}>Timeline</em>
              </h3>
              <p className="gt-page-sub" style={{ marginTop: 2, fontSize: 12 }}>
                Peak on <span className="gt-display" style={{ color: 'var(--gt-text-primary)' }}>{fmtDate(peakPoint.period)}</span>
                {' '}· grouped <em className="gt-serif-italic">by {timelineGroupBy}</em>
              </p>
            </div>
            <div className="w-[180px]">
              <Select
                value={timelineGroupBy}
                onValueChange={(v) => setTimelineGroupBy(v as 'month' | 'week')}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="month">Group by Month</SelectItem>
                  <SelectItem value="week">Group by Week</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="overflow-x-auto gt-scroll" style={{ paddingBottom: 8 }}>
            <div style={{ minWidth: Math.max(timeline.length * 96, 720), padding: '0 6px' }}>
              {/* Y-axis grid lines + bars */}
              <div style={{ position: 'relative', height: 280 }}>
                {/* horizontal grid */}
                {[1, 0.75, 0.5, 0.25, 0].map((p, i) => (
                  <div key={i} style={{
                    position: 'absolute', left: 0, right: 0, bottom: `${p * 240}px`,
                    height: 1,
                    background: i === 4 ? 'var(--gt-border-strong)' : 'var(--gt-divider)',
                    pointerEvents: 'none',
                  }}>
                    <span className="gt-numerals" style={{
                      position: 'absolute', left: 0, top: -8,
                      fontSize: 9.5, color: 'var(--gt-text-tertiary)',
                      fontFamily: 'var(--gt-font-display)', fontWeight: 600,
                    }}>
                      {Math.round(maxValue * p)}
                    </span>
                  </div>
                ))}

                {/* bars */}
                <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'flex-end', justifyContent: 'space-around', gap: 12, paddingLeft: 28, paddingBottom: 0 }}>
                  {timeline.map((point, idx) => {
                    const createdH = (point.created / maxValue) * 240;
                    const completedH = (point.completed / maxValue) * 240;
                    const overdueH = (point.overdue / maxValue) * 240;
                    return (
                      <div key={idx} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1, minWidth: 64 }}>
                        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 4, height: 240 }}>
                          {/* Created — blue */}
                          <div title={`Created: ${point.created}`} style={{
                            width: 18, height: createdH, minHeight: point.created > 0 ? 6 : 0,
                            background: 'linear-gradient(180deg, #93c5fd 0%, #2563eb 100%)',
                            borderRadius: '6px 6px 0 0',
                            boxShadow: '0 4px 10px -3px rgba(37, 99, 235, 0.45), inset 0 1px 0 rgba(255,255,255,0.45)',
                            transition: 'height 600ms cubic-bezier(0.4,0,0.2,1)',
                            position: 'relative',
                            display: 'flex', justifyContent: 'center',
                          }}>
                            {point.created > 0 && (
                              <span className="gt-display gt-numerals" style={{
                                position: 'absolute', top: -18, fontSize: 10, fontWeight: 800, color: '#1e3a8a',
                              }}>{point.created}</span>
                            )}
                          </div>
                          {/* Completed — emerald */}
                          <div title={`Completed: ${point.completed}`} style={{
                            width: 18, height: completedH, minHeight: point.completed > 0 ? 6 : 0,
                            background: 'linear-gradient(180deg, #6ee7b7 0%, #10b981 50%, #047857 100%)',
                            borderRadius: '6px 6px 0 0',
                            boxShadow: '0 4px 10px -3px rgba(16, 185, 129, 0.45), inset 0 1px 0 rgba(255,255,255,0.45)',
                            transition: 'height 600ms cubic-bezier(0.4,0,0.2,1)',
                            position: 'relative',
                            display: 'flex', justifyContent: 'center',
                          }}>
                            {point.completed > 0 && (
                              <span className="gt-display gt-numerals" style={{
                                position: 'absolute', top: -18, fontSize: 10, fontWeight: 800, color: '#064e3b',
                              }}>{point.completed}</span>
                            )}
                          </div>
                          {/* Overdue — red */}
                          <div title={`Overdue: ${point.overdue}`} style={{
                            width: 18, height: overdueH, minHeight: point.overdue > 0 ? 6 : 0,
                            background: 'linear-gradient(180deg, #fca5a5 0%, #ef4444 100%)',
                            borderRadius: '6px 6px 0 0',
                            boxShadow: '0 4px 10px -3px rgba(239, 68, 68, 0.45), inset 0 1px 0 rgba(255,255,255,0.45)',
                            transition: 'height 600ms cubic-bezier(0.4,0,0.2,1)',
                            position: 'relative',
                            display: 'flex', justifyContent: 'center',
                          }}>
                            {point.overdue > 0 && (
                              <span className="gt-display gt-numerals" style={{
                                position: 'absolute', top: -18, fontSize: 10, fontWeight: 800, color: '#7f1d1d',
                              }}>{point.overdue}</span>
                            )}
                          </div>
                        </div>
                        <span className="gt-numerals" style={{
                          marginTop: 8, fontSize: 11, fontFamily: 'var(--gt-font-display)', fontWeight: 600,
                          color: 'var(--gt-text-secondary)', whiteSpace: 'nowrap',
                        }}>
                          {fmtDate(point.period)}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>

          {/* Legend */}
          <div className="flex justify-center gap-6 mt-6 pt-4" style={{ borderTop: '1px dashed var(--gt-border-strong)' }}>
            {[
              { label: 'Created',   bg: 'linear-gradient(180deg, #93c5fd 0%, #2563eb 100%)' },
              { label: 'Completed', bg: 'linear-gradient(180deg, #6ee7b7 0%, #10b981 50%, #047857 100%)' },
              { label: 'Overdue',   bg: 'linear-gradient(180deg, #fca5a5 0%, #ef4444 100%)' },
            ].map((l) => (
              <div key={l.label} className="flex items-center gap-2">
                <span style={{ width: 12, height: 12, borderRadius: 4, background: l.bg, boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.4)' }} />
                <span className="gt-eyebrow" style={{ marginBottom: 0, color: 'var(--gt-text-secondary)' }}>{l.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  // ── Clients Tab ──────────────────────────────────────────────────────
  const getStatusColor = (status: string) => {
    const m: Record<string, string> = {
      DONE: 'bg-green-100 text-green-800', IN_PROGRESS: 'bg-blue-100 text-blue-800',
      BLOCKED: 'bg-red-100 text-red-800', TODO: 'bg-gray-100 text-gray-800',
      IN_REVIEW: 'bg-purple-100 text-purple-800',
    };
    return m[status] || 'bg-yellow-100 text-yellow-800';
  };
  const getPriorityColor = (p: string) => {
    const m: Record<string, string> = {
      URGENT: 'bg-red-100 text-red-800', HIGH: 'bg-orange-100 text-orange-800',
      MEDIUM: 'bg-yellow-100 text-yellow-800', LOW: 'bg-blue-100 text-blue-800',
    };
    return m[p] || 'bg-gray-100 text-gray-800';
  };
  const avatarGrad = (name: string) => {
    const g = ['from-violet-500 to-purple-600', 'from-blue-500 to-cyan-600', 'from-emerald-500 to-green-600', 'from-orange-500 to-red-500', 'from-pink-500 to-rose-600', 'from-indigo-500 to-blue-600'];
    return g[name.charCodeAt(0) % g.length];
  };

  // ── Projects Tab ─────────────────────────────────────────────────────
  const renderProjects = () => {
    if (loadingProjects) return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4" />
          <p className="text-gray-500">Loading project report…</p>
        </div>
      </div>
    );

    if (projectReport.length === 0) return (
      <div className="bg-white rounded-2xl shadow-md border border-gray-100 p-16 text-center">
        <div className="w-20 h-20 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <Folder className="w-10 h-10 text-indigo-400" />
        </div>
        <h3 className="text-xl font-semibold text-gray-700 mb-2">No project data</h3>
        <p className="text-gray-400">No tasks have been linked to projects yet, or try adjusting filters.</p>
      </div>
    );

    const totalTasks = projectReport.reduce((s, p) => s + p.total, 0);
    const totalDone = projectReport.reduce((s, p) => s + p.completed, 0);
    const totalOD = projectReport.reduce((s, p) => s + p.overdue, 0);
    const avgRate = projectReport.length ? Math.round(projectReport.reduce((s, p) => s + p.completion_rate, 0) / projectReport.length) : 0;

    const statusBadge = (status: string | null) => {
      if (!status) return null;
      const meta: Record<string, { bg: string; color: string }> = {
        ACTIVE:    { bg: 'rgba(22,163,74,0.10)',  color: '#16a34a' },
        ON_HOLD:   { bg: 'rgba(217,119,6,0.10)',  color: '#d97706' },
        COMPLETED: { bg: 'rgba(37,99,235,0.10)',  color: '#2563eb' },
        ARCHIVED:  { bg: 'rgba(107,114,128,0.10)', color: '#6b7280' },
      };
      const m = meta[status] || meta.ACTIVE;
      return (
        <span className="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full font-bold"
              style={{ background: m.bg, color: m.color }}>
          {status.replace('_', ' ')}
        </span>
      );
    };

    return (
      <div className="space-y-6">
        {/* Summary Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { label: 'Active Projects', value: projectReport.length, color: 'indigo' },
            { label: 'Total Tasks', value: totalTasks, color: 'blue' },
            { label: 'Completed', value: totalDone, color: 'emerald' },
            { label: 'Avg Completion', value: `${avgRate}%`, color: 'purple' },
          ].map(s => (
            <div key={s.label} className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100 flex items-center gap-4 hover:shadow-md transition-shadow">
              <div className={`w-12 h-12 bg-${s.color}-100 rounded-xl flex items-center justify-center flex-shrink-0`}>
                <Folder className={`w-6 h-6 text-${s.color}-600`} />
              </div>
              <div>
                <p className="text-xs text-gray-500">{s.label}</p>
                <p className={`text-2xl font-bold text-${s.color}-700`}>{s.value}</p>
              </div>
            </div>
          ))}
        </div>

        {totalOD > 0 && (
          <div className="flex items-center gap-3 bg-red-50 border border-red-200 rounded-xl px-5 py-3 text-red-700 text-sm">
            <AlertTriangle className="w-5 h-5 flex-shrink-0" />
            <span><strong>{totalOD}</strong> overdue task{totalOD !== 1 ? 's' : ''} across all projects need attention.</span>
          </div>
        )}

        {/* Project Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
          {projectReport.map(p => {
            const rate = p.completion_rate;
            const rateColor = rate >= 75 ? 'text-emerald-600' : rate >= 40 ? 'text-yellow-600' : 'text-red-500';
            const barColor = rate >= 75 ? 'from-emerald-400 to-green-500' : rate >= 40 ? 'from-yellow-400 to-orange-400' : 'from-red-400 to-rose-500';
            const isSelected = selectedProjectId === p.project_id;
            return (
              <div key={p.project_id}
                className={`bg-white rounded-2xl shadow-sm border transition-all duration-200 overflow-hidden cursor-pointer ${isSelected ? 'border-indigo-400 ring-2 ring-indigo-200 shadow-lg' : 'border-gray-100 hover:shadow-md hover:border-indigo-200'}`}
                onClick={() => {
                  if (isSelected) { setSelectedProjectId(null); return; }
                  setSelectedProjectId(p.project_id);
                  setSelectedProjectName(p.project_name);
                  setProjectTaskStatusFilter('');
                  loadProjectTasksFor(p.project_id, '');
                }}
              >
                <div className={`h-1.5 bg-gradient-to-r ${avatarGrad(p.project_name)}`} />
                <div className="p-5">
                  <div className="flex items-start gap-3 mb-4">
                    <div className={`w-11 h-11 bg-gradient-to-br ${avatarGrad(p.project_name)} rounded-xl flex items-center justify-center text-white font-bold text-lg shadow-sm flex-shrink-0`}>
                      <Folder className="w-5 h-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <h3 className="font-bold text-gray-900 truncate">{p.project_name}</h3>
                        {statusBadge(p.project_status)}
                      </div>
                      <p className="text-xs text-gray-400 mt-0.5">
                        {p.project_code && <span className="font-mono mr-2">{p.project_code}</span>}
                        {p.client_name && <span>· <Building2 className="inline w-3 h-3 mr-0.5" />{p.client_name}</span>}
                      </p>
                    </div>
                    <span className={`text-lg font-extrabold ${rateColor}`}>{rate}%</span>
                  </div>

                  <div className="mb-4">
                    <div className="flex justify-between text-xs text-gray-500 mb-1">
                      <span>Completion</span>
                      <span>{p.completed}/{p.total}</span>
                    </div>
                    <div className="w-full bg-gray-100 rounded-full h-2">
                      <div className={`bg-gradient-to-r ${barColor} h-2 rounded-full transition-all duration-700`} style={{ width: `${rate}%` }} />
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-2 text-center">
                    <div className="bg-green-50 rounded-lg py-2">
                      <p className="text-lg font-bold text-green-700">{p.completed}</p>
                      <p className="text-xs text-green-600">Done</p>
                    </div>
                    <div className="bg-blue-50 rounded-lg py-2">
                      <p className="text-lg font-bold text-blue-700">{p.pending}</p>
                      <p className="text-xs text-blue-600">Pending</p>
                    </div>
                    <div className={`${p.overdue > 0 ? 'bg-red-50' : 'bg-gray-50'} rounded-lg py-2`}>
                      <p className={`text-lg font-bold ${p.overdue > 0 ? 'text-red-600' : 'text-gray-400'}`}>{p.overdue}</p>
                      <p className={`text-xs ${p.overdue > 0 ? 'text-red-500' : 'text-gray-400'}`}>Overdue</p>
                    </div>
                  </div>

                  <div className="mt-3 pt-3 border-t border-gray-100 flex justify-center">
                    <span className={`text-xs font-medium ${isSelected ? 'text-indigo-600' : 'text-gray-400'}`}>
                      {isSelected ? '▲ Hide Tasks' : '▼ View Tasks'}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Drill-down */}
        {selectedProjectId && (
          <div className="bg-white rounded-2xl shadow-md border border-indigo-100 overflow-hidden">
            <div className="bg-gradient-to-r from-indigo-50 to-purple-50 px-6 py-4 flex items-center justify-between border-b border-indigo-100">
              <div className="flex items-center gap-3">
                <div className={`w-9 h-9 bg-gradient-to-br ${avatarGrad(selectedProjectName)} rounded-xl flex items-center justify-center text-white`}>
                  <Folder className="w-4 h-4" />
                </div>
                <div>
                  <h3 className="font-bold text-gray-900">{selectedProjectName}</h3>
                  <p className="text-xs text-gray-500">{projectTasks.length} tasks</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-[180px]">
                  <Select
                    value={projectTaskStatusFilter || "__all__"}
                    onValueChange={(v) => {
                      const val = v === "__all__" ? "" : v;
                      setProjectTaskStatusFilter(val);
                      loadProjectTasksFor(selectedProjectId, val);
                    }}
                  >
                    <SelectTrigger><SelectValue placeholder="All Statuses" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__all__">All Statuses</SelectItem>
                      <SelectItem value="TODO">Todo</SelectItem>
                      <SelectItem value="IN_PROGRESS">In Progress</SelectItem>
                      <SelectItem value="IN_REVIEW">In Review</SelectItem>
                      <SelectItem value="DONE">Done</SelectItem>
                      <SelectItem value="BLOCKED">Blocked</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <button onClick={() => setSelectedProjectId(null)} className="p-1.5 hover:bg-white/60 rounded-lg text-gray-400 hover:text-gray-600">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
              </div>
            </div>

            {loadingProjectTasks ? (
              <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" /></div>
            ) : projectTasks.length === 0 ? (
              <div className="text-center py-12 text-gray-400">No tasks found</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-100">
                  <thead className="bg-gray-50">
                    <tr>
                      {['Task', 'Status', 'Priority', 'Assignee', 'Progress', 'Due Date'].map(h => (
                        <th key={h} className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-50">
                    {projectTasks.map(t => (
                      <tr key={t.id} className="hover:bg-indigo-50/30 transition-colors">
                        <td className="px-5 py-3.5">
                          <p className="font-medium text-gray-900 text-sm">{t.title}</p>
                          <p className="text-xs text-gray-400 mt-0.5">{t.created_at ? new Date(t.created_at).toLocaleDateString() : '—'}</p>
                        </td>
                        <td className="px-5 py-3.5">
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(t.status)}`}>{t.status.replace('_', ' ')}</span>
                        </td>
                        <td className="px-5 py-3.5">
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${getPriorityColor(t.priority)}`}>{t.priority}</span>
                        </td>
                        <td className="px-5 py-3.5 text-sm text-gray-700">{t.primary_assignee_name || t.created_by_name || '—'}</td>
                        <td className="px-5 py-3.5">
                          <div className="flex items-center gap-2">
                            <div className="w-20 bg-gray-100 rounded-full h-1.5">
                              <div className="bg-gradient-to-r from-indigo-400 to-purple-500 h-1.5 rounded-full" style={{ width: `${displayedProgress(t.progress_percent, t.status)}%` }} />
                            </div>
                            <span className="text-xs text-gray-500 w-8">{displayedProgress(t.progress_percent, t.status)}%</span>
                          </div>
                        </td>
                        <td className="px-5 py-3.5 text-sm">
                          {t.due_date ? (
                            <span className={t.is_overdue ? 'text-red-600 font-semibold' : 'text-gray-600'}>
                              {new Date(t.due_date).toLocaleDateString()}
                              {t.is_overdue && <span className="ml-1 text-xs bg-red-100 text-red-700 px-1.5 py-0.5 rounded">Overdue</span>}
                            </span>
                          ) : <span className="text-gray-400">—</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderClients = () => {
    if (loadingClients) return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4" />
          <p className="text-gray-500">Loading client report…</p>
        </div>
      </div>
    );

    if (clientReport.length === 0) return (
      <div className="bg-white rounded-2xl shadow-md border border-gray-100 p-16 text-center">
        <div className="w-20 h-20 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg className="w-10 h-10 text-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
        </div>
        <h3 className="text-xl font-semibold text-gray-700 mb-2">No client data</h3>
        <p className="text-gray-400">No tasks have been linked to clients yet, or try adjusting filters.</p>
      </div>
    );

    // Summary row
    const totalTasks = clientReport.reduce((s, c) => s + c.total, 0);
    const totalDone = clientReport.reduce((s, c) => s + c.completed, 0);
    const totalOD = clientReport.reduce((s, c) => s + c.overdue, 0);
    const avgRate = clientReport.length ? Math.round(clientReport.reduce((s, c) => s + c.completion_rate, 0) / clientReport.length) : 0;

    return (
      <div className="space-y-6">
        {/* Summary Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { label: 'Active Clients', value: clientReport.length, color: 'indigo', icon: 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z' },
            { label: 'Total Tasks', value: totalTasks, color: 'blue', icon: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2' },
            { label: 'Completed', value: totalDone, color: 'emerald', icon: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z' },
            { label: 'Avg Completion', value: `${avgRate}%`, color: 'purple', icon: 'M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z' },
          ].map(s => (
            <div key={s.label} className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100 flex items-center gap-4 hover:shadow-md transition-shadow">
              <div className={`w-12 h-12 bg-${s.color}-100 rounded-xl flex items-center justify-center flex-shrink-0`}>
                <svg className={`w-6 h-6 text-${s.color}-600`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={s.icon} />
                </svg>
              </div>
              <div>
                <p className="text-xs text-gray-500">{s.label}</p>
                <p className={`text-2xl font-bold text-${s.color}-700`}>{s.value}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Overdue alert if any */}
        {totalOD > 0 && (
          <div className="flex items-center gap-3 bg-red-50 border border-red-200 rounded-xl px-5 py-3 text-red-700 text-sm">
            <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
            <span><strong>{totalOD}</strong> overdue task{totalOD !== 1 ? 's' : ''} across all clients need attention.</span>
          </div>
        )}

        {/* Client Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
          {clientReport.map(c => {
            const rate = c.completion_rate;
            const rateColor = rate >= 75 ? 'text-emerald-600' : rate >= 40 ? 'text-yellow-600' : 'text-red-500';
            const barColor = rate >= 75 ? 'from-emerald-400 to-green-500' : rate >= 40 ? 'from-yellow-400 to-orange-400' : 'from-red-400 to-rose-500';
            const isSelected = selectedClientId === c.client_id;
            return (
              <div key={c.client_id}
                className={`bg-white rounded-2xl shadow-sm border transition-all duration-200 overflow-hidden cursor-pointer ${isSelected ? 'border-indigo-400 ring-2 ring-indigo-200 shadow-lg' : 'border-gray-100 hover:shadow-md hover:border-indigo-200'}`}
                onClick={() => {
                  if (isSelected) { setSelectedClientId(null); return; }
                  setSelectedClientId(c.client_id);
                  setSelectedClientName(c.client_name);
                  setClientTaskStatusFilter('');
                  loadClientTasksFor(c.client_id, '');
                }}
              >
                {/* Card top accent */}
                <div className={`h-1.5 bg-gradient-to-r ${avatarGrad(c.client_name)}`} />
                <div className="p-5">
                  <div className="flex items-start gap-3 mb-4">
                    <div className={`w-11 h-11 bg-gradient-to-br ${avatarGrad(c.client_name)} rounded-xl flex items-center justify-center text-white font-bold text-lg shadow-sm flex-shrink-0`}>
                      {c.client_name.charAt(0).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-bold text-gray-900 truncate">{c.client_name}</h3>
                      <p className="text-xs text-gray-400">{c.total} task{c.total !== 1 ? 's' : ''} total</p>
                    </div>
                    <span className={`text-lg font-extrabold ${rateColor}`}>{rate}%</span>
                  </div>

                  {/* Progress bar */}
                  <div className="mb-4">
                    <div className="flex justify-between text-xs text-gray-500 mb-1">
                      <span>Completion</span>
                      <span>{c.completed}/{c.total}</span>
                    </div>
                    <div className="w-full bg-gray-100 rounded-full h-2">
                      <div className={`bg-gradient-to-r ${barColor} h-2 rounded-full transition-all duration-700`} style={{ width: `${rate}%` }} />
                    </div>
                  </div>

                  {/* Metrics row */}
                  <div className="grid grid-cols-3 gap-2 text-center">
                    <div className="bg-green-50 rounded-lg py-2">
                      <p className="text-lg font-bold text-green-700">{c.completed}</p>
                      <p className="text-xs text-green-600">Done</p>
                    </div>
                    <div className="bg-blue-50 rounded-lg py-2">
                      <p className="text-lg font-bold text-blue-700">{c.pending}</p>
                      <p className="text-xs text-blue-600">Pending</p>
                    </div>
                    <div className={`${c.overdue > 0 ? 'bg-red-50' : 'bg-gray-50'} rounded-lg py-2`}>
                      <p className={`text-lg font-bold ${c.overdue > 0 ? 'text-red-600' : 'text-gray-400'}`}>{c.overdue}</p>
                      <p className={`text-xs ${c.overdue > 0 ? 'text-red-500' : 'text-gray-400'}`}>Overdue</p>
                    </div>
                  </div>

                  <div className="mt-3 pt-3 border-t border-gray-100 flex justify-center">
                    <span className={`text-xs font-medium ${isSelected ? 'text-indigo-600' : 'text-gray-400'} flex items-center gap-1`}>
                      {isSelected ? (
                        <><svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" /></svg> Hide Tasks</>
                      ) : (
                        <><svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg> View Tasks</>
                      )}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Task Drill-down Panel */}
        {selectedClientId && (
          <div className="bg-white rounded-2xl shadow-md border border-indigo-100 overflow-hidden">
            <div className="bg-gradient-to-r from-indigo-50 to-purple-50 px-6 py-4 flex items-center justify-between border-b border-indigo-100">
              <div className="flex items-center gap-3">
                <div className={`w-9 h-9 bg-gradient-to-br ${avatarGrad(selectedClientName)} rounded-xl flex items-center justify-center text-white font-bold`}>
                  {selectedClientName.charAt(0).toUpperCase()}
                </div>
                <div>
                  <h3 className="font-bold text-gray-900">{selectedClientName}</h3>
                  <p className="text-xs text-gray-500">{clientTasks.length} tasks</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-[180px]">
                  <Select
                    value={clientTaskStatusFilter || "__all__"}
                    onValueChange={(v) => {
                      const val = v === "__all__" ? "" : v;
                      setClientTaskStatusFilter(val);
                      loadClientTasksFor(selectedClientId, val);
                    }}
                  >
                    <SelectTrigger><SelectValue placeholder="All Statuses" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__all__">All Statuses</SelectItem>
                      <SelectItem value="TODO">Todo</SelectItem>
                      <SelectItem value="IN_PROGRESS">In Progress</SelectItem>
                      <SelectItem value="IN_REVIEW">In Review</SelectItem>
                      <SelectItem value="DONE">Done</SelectItem>
                      <SelectItem value="BLOCKED">Blocked</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <button onClick={() => setSelectedClientId(null)} className="p-1.5 hover:bg-white/60 rounded-lg text-gray-400 hover:text-gray-600 transition-colors">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
              </div>
            </div>

            {loadingClientTasks ? (
              <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" /></div>
            ) : clientTasks.length === 0 ? (
              <div className="text-center py-12 text-gray-400">No tasks found</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-100">
                  <thead className="bg-gray-50">
                    <tr>
                      {['Task', 'Project', 'Status', 'Priority', 'Assignee', 'Progress', 'Due Date'].map(h => (
                        <th key={h} className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-50">
                    {clientTasks.map(t => (
                      <tr key={t.id} className="hover:bg-indigo-50/30 transition-colors">
                        <td className="px-5 py-3.5">
                          <p className="font-medium text-gray-900 text-sm">{t.title}</p>
                          <p className="text-xs text-gray-400 mt-0.5">{t.created_at ? new Date(t.created_at).toLocaleDateString() : '—'}</p>
                        </td>
                        <td className="px-5 py-3.5">
                          {t.project_name ? (
                            <button
                              onClick={(e) => { e.stopPropagation(); navigate(`/projects/${t.project_id}`); }}
                              className="inline-flex items-center gap-1 text-xs font-medium text-indigo-600 hover:text-indigo-800"
                            >
                              <Folder className="w-3 h-3" />
                              {t.project_name}
                            </button>
                          ) : <span className="text-xs text-gray-400">—</span>}
                        </td>
                        <td className="px-5 py-3.5">
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(t.status)}`}>{t.status.replace('_', ' ')}</span>
                        </td>
                        <td className="px-5 py-3.5">
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${getPriorityColor(t.priority)}`}>{t.priority}</span>
                        </td>
                        <td className="px-5 py-3.5 text-sm text-gray-700">{t.primary_assignee_name || t.created_by_name || '—'}</td>
                        <td className="px-5 py-3.5">
                          <div className="flex items-center gap-2">
                            <div className="w-20 bg-gray-100 rounded-full h-1.5">
                              <div className="bg-gradient-to-r from-indigo-400 to-purple-500 h-1.5 rounded-full" style={{ width: `${displayedProgress(t.progress_percent, t.status)}%` }} />
                            </div>
                            <span className="text-xs text-gray-500 w-8">{displayedProgress(t.progress_percent, t.status)}%</span>
                          </div>
                        </td>
                        <td className="px-5 py-3.5 text-sm">
                          {t.due_date ? (
                            <span className={t.is_overdue ? 'text-red-600 font-semibold' : 'text-gray-600'}>
                              {new Date(t.due_date).toLocaleDateString()}
                              {t.is_overdue && <span className="ml-1 text-xs bg-red-100 text-red-700 px-1.5 py-0.5 rounded">Overdue</span>}
                            </span>
                          ) : <span className="text-gray-400">—</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  // Error display
  // if (error) {
  //   return (
  //     <div className="flex items-center justify-center min-h-[60vh]">
  //       <div className="text-center bg-red-50 border border-red-200 rounded-lg p-6 max-w-md">
  //         <div className="text-red-500 text-5xl mb-4">⚠️</div>
  //         <p className="text-red-600 font-medium">{error}</p>
  //         <button
  //           onClick={() => window.location.reload()}
  //           className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
  //         >
  //           Try Again
  //         </button>
  //       </div>
  //     </div>
  //   );
  // }

  return (
    <div className="gt-themed gt-shell min-h-screen p-6">
      <div className="mb-4 pb-3" style={{ borderBottom: '1px dashed var(--gt-border-strong)' }}>
        <div className="flex justify-between items-end flex-wrap gap-4">
          <div>
            <div className="gt-eyebrow-row">
              <span className="gt-leaf" />
              <span className="gt-eyebrow">iii. Insights</span>
            </div>
            <h1 className="gt-page-title">
              Reports <em>Management</em>
            </h1>
            <p className="gt-page-sub" style={{ marginTop: 4 }}>
              Comprehensive <em className="gt-serif-italic">analytics</em> and insights for task management.
            </p>
          </div>
        </div>
      </div>

      {/* Filters Section */}
      <div className="bg-white rounded-xl shadow-md border border-gray-100 p-4 mb-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
            <DateField
              value={tempFilters.start_date || ''}
              onChange={(v) => handleFilterChange('start_date', v)}
              placeholder="Pick a start date"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
            <DateField
              value={tempFilters.end_date || ''}
              onChange={(v) => handleFilterChange('end_date', v)}
              placeholder="Pick an end date"
            />
          </div>
          {/* <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Department ID</label>
            <input
              type="text"
              placeholder="Department ID"
              value={tempFilters.department_id || ''}
              onChange={(e) => handleFilterChange('department_id', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div> */}
        </div>
        <div className="flex gap-3 mt-4">
          <button
            onClick={applyFilters}
            className="px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg hover:from-blue-700 hover:to-blue-800 transition-all duration-200 shadow-md"
          >
            Apply Filters
          </button>
          <button
            onClick={resetFilters}
            className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
          >
            Reset
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ borderBottom: '1px solid var(--gt-border)', marginBottom: 24 }}>
        <nav className="flex gap-1 flex-wrap">
          {[
            { id: 'overview' as TabType,  name: 'Overview',  icon: <BarChart3 className="w-4 h-4" /> },
            { id: 'projects' as TabType,  name: 'Projects',  icon: <Folder className="w-4 h-4" /> },
            { id: 'tasks' as TabType,     name: 'Tasks',     icon: <FileText className="w-4 h-4" /> },
            { id: 'clients' as TabType,   name: 'Clients',   icon: <Building2 className="w-4 h-4" /> },
            { id: 'employees' as TabType, name: 'Employees', icon: <UsersIcon className="w-4 h-4" /> },
            { id: 'workload' as TabType,  name: 'Workload',  icon: <Zap className="w-4 h-4" /> },
            { id: 'timeline' as TabType,  name: 'Timeline',  icon: <CalIcon className="w-4 h-4" /> },
          ].map((tab) => {
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className="transition-all"
                style={{
                  padding: '10px 18px',
                  fontFamily: 'var(--gt-font-display)',
                  fontSize: 13,
                  fontWeight: 600,
                  letterSpacing: '0.01em',
                  borderTopLeftRadius: 12,
                  borderTopRightRadius: 12,
                  border: 'none',
                  borderBottom: isActive ? '2px solid var(--gt-primary)' : '2px solid transparent',
                  background: isActive ? 'var(--gt-surface-0)' : 'transparent',
                  color: isActive ? 'var(--gt-primary-deep)' : 'var(--gt-text-tertiary)',
                  boxShadow: isActive ? 'var(--gt-elev-1)' : 'none',
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 8,
                  marginBottom: -1,
                }}
              >
                {tab.icon}
                {tab.name}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Tab Content */}
      <div>
        {activeTab === 'overview' && renderOverview()}
        {activeTab === 'employees' && renderEmployees()}
        {activeTab === 'tasks' && renderTasks()}
        {activeTab === 'workload' && renderWorkload()}
        {activeTab === 'timeline' && renderTimeline()}
        {activeTab === 'clients' && renderClients()}
        {activeTab === 'projects' && renderProjects()}
      </div>

      {/* Employee Detail Modal */}
      {isEmployeeModalOpen && selectedEmployee && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75" onClick={() => setIsEmployeeModalOpen(false)}></div>
            </div>

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">{selectedEmployee.name}</h3>
                    <p className="text-sm text-gray-500">{selectedEmployee.email}</p>
                  </div>
                  <button
                    onClick={() => setIsEmployeeModalOpen(false)}
                    className="text-gray-400 hover:text-gray-500"
                  >
                    <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-xs text-gray-500">Total Assigned</p>
                    <p className="text-xl font-bold">{selectedEmployee.total_assigned}</p>
                  </div>
                  <div className="bg-green-50 rounded-lg p-3">
                    <p className="text-xs text-green-600">Completed</p>
                    <p className="text-xl font-bold text-green-700">{selectedEmployee.done}</p>
                  </div>
                  <div className="bg-red-50 rounded-lg p-3">
                    <p className="text-xs text-red-600">Overdue</p>
                    <p className="text-xl font-bold text-red-700">{selectedEmployee.overdue}</p>
                  </div>
                  <div className="bg-blue-50 rounded-lg p-3">
                    <p className="text-xs text-blue-600">Completion Rate</p>
                    <p className="text-xl font-bold text-blue-700">{selectedEmployee.completion_rate_pct}%</p>
                  </div>
                </div>

                <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                  <button
                    type="button"
                    onClick={() => setIsEmployeeModalOpen(false)}
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default TaskReports;